import { ThreadListItemPrimitive, ThreadListPrimitive } from '@assistant-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Icon, IconButton, Stack, styled, Text } from 'shell/components';
import ChatThreadTitle from '../ChatThreadTitle';

const HugoThreadSidebar = ({ isExpanded, threadSwitcher, onDeleteThread }) => {
    const { t } = useTranslation();

    const scopeLabelById = useMemo(() => {
        return threadSwitcher.items.reduce((labels, thread) => {
            labels[thread.id] = thread.scopeLabel;
            return labels;
        }, {});
    }, [threadSwitcher.items]);

    return (
        <SidebarRoot as='aside' aria-label={'Hugo chat threads'} isExpanded={isExpanded}>
            <ThreadPanelContent aria-hidden={!isExpanded} isExpanded={isExpanded}>
                <ThreadListPanel spacing={3}>
                    <ThreadListRoot>
                        <ThreadListControls spacing={2}>
                            <NewChatButton
                                disabled={threadSwitcher.disabled}
                                size={'small'}
                                startIcon={<Icon kind={'add'} />}
                                tabIndex={isExpanded ? undefined : -1}
                                type={'button'}
                                variant={'tertiary'}
                                onClick={threadSwitcher.onStartNew}
                            >
                                {t('chat.hugo.new-chat', 'New Chat')}
                            </NewChatButton>
                        </ThreadListControls>
                        <Divider sx={{ mx: 2 }} />

                        {threadSwitcher.isLoading ? (
                            <ThreadListStatus color={'light'} variant={'p3'}>
                                {t('chat.hugo.loading-chats', 'Loading Chats...')}
                            </ThreadListStatus>
                        ) : null}

                        {!threadSwitcher.isLoading && threadSwitcher.items.length === 0 ? (
                            <ThreadListStatus color={'light'} variant={'p3'}>
                                {t('chat.hugo.no-saved-chats', 'No Saved Chats')}
                            </ThreadListStatus>
                        ) : null}

                        {!threadSwitcher.isLoading && threadSwitcher.items.length > 0 ? (
                            <ThreadListHeader paddingBlock={1.5} paddingInline={2}>
                                <Text color={'light'}>
                                    {t('chat.hugo.recent-conversations', 'Recent Conversations')}
                                </Text>
                            </ThreadListHeader>
                        ) : null}

                        {!threadSwitcher.isLoading ? (
                            <ThreadListItemsContainer>
                                <ThreadListPrimitive.Items>
                                    {({ threadListItem }) => (
                                        <ThreadListItemPrimitive.Root asChild>
                                            <ThreadListItemRow>
                                                <ThreadListItemTrigger
                                                    disabled={threadSwitcher.disabled}
                                                    tabIndex={isExpanded ? undefined : -1}
                                                    type={'button'}
                                                    onClick={() => {
                                                        if (threadSwitcher.disabled) return;
                                                        threadSwitcher.onSelect?.(threadListItem.id);
                                                    }}
                                                >
                                                    <ChatThreadTitle noWrap />
                                                    {scopeLabelById[threadListItem.id] ? (
                                                        <ThreadScope noWrap>
                                                            {scopeLabelById[threadListItem.id]}
                                                        </ThreadScope>
                                                    ) : null}
                                                </ThreadListItemTrigger>
                                                <DeleteActionContainer className='delete-action-container'>
                                                    <IconButton
                                                        aria-label={t('chat.hugo.delete-chat', 'Delete Chat')}
                                                        kind={'delete'}
                                                        size={'small'}
                                                        tabIndex={isExpanded ? undefined : -1}
                                                        variant={'square'}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            onDeleteThread?.(threadListItem.id);
                                                        }}
                                                    />
                                                </DeleteActionContainer>
                                            </ThreadListItemRow>
                                        </ThreadListItemPrimitive.Root>
                                    )}
                                </ThreadListPrimitive.Items>
                            </ThreadListItemsContainer>
                        ) : null}
                    </ThreadListRoot>
                </ThreadListPanel>
            </ThreadPanelContent>
        </SidebarRoot>
    );
};

const SidebarRoot = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ isExpanded, theme }) => ({
    position: 'absolute',
    top: '50px',
    bottom: 0,
    left: 0,
    zIndex: 4,
    width: '100%',
    gap: theme.spacing(4),
    minHeight: 0,
    overflow: 'hidden',
    borderRight: isExpanded ? `1px solid ${theme.palette.ensightful.neutral[200]}` : '1px solid transparent',
    background: isExpanded ? theme.palette.ensightful.neutral[50] : 'transparent',
    boxShadow: isExpanded ? theme.palette.ensightful.shadows[2] : 'none',
    transform: isExpanded ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 220ms ease, background 220ms ease, border-color 220ms ease, box-shadow 220ms ease',
    pointerEvents: isExpanded ? 'auto' : 'none',
    paddingBlock: '16px',
}));

const ThreadPanelContent = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ isExpanded }) => ({
    width: '100%',
    minHeight: 0,
    maxHeight: '100%',
    opacity: 1,
    pointerEvents: isExpanded ? 'auto' : 'none',
}));

const ThreadListPanel = styled(Stack)(({ theme }) => ({
    height: '100%',
    minHeight: 0,
    padding: theme.spacing(0, 3, 3),
}));

const ThreadListControls = styled(Stack)(() => ({
    flexShrink: 0,
}));

const ThreadListRoot = styled(ThreadListPrimitive.Root)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    minHeight: 0,
}));

const threadListReveal = {
    animation: 'hugo-thread-list-reveal 240ms ease-out',
    '@keyframes hugo-thread-list-reveal': {
        from: {
            opacity: 0,
        },
        to: {
            opacity: 1,
        },
    },
    '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
    },
};

const ThreadListHeader = styled(Stack)(() => ({
    ...threadListReveal,
}));

const ThreadListItemsContainer = styled(Stack)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1),
    margin: theme.spacing(-1),
    ...threadListReveal,
}));

const ThreadListStatus = styled(Text)(({ theme }) => ({
    padding: theme.spacing(1),
}));

const ThreadListItemRow = styled(Stack)(({ theme }) => ({
    width: '100%',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',

    '&[data-active="true"]': {
        background: theme.palette.ensightful.primary[50],
    },
    '&:hover': {
        background: theme.palette.ensightful.neutral[100],
    },
    '&[data-active="true"]:hover': {
        background: theme.palette.ensightful.primary[50],
    },

    '&:hover .delete-action-container, &:focus-within .delete-action-container': {
        opacity: 1,
        pointerEvents: 'auto',
    },
}));

const ThreadListItemTrigger = styled('button')(({ disabled, theme }) => ({
    width: '100%',
    minWidth: 0,
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    color: theme.palette.ensightful.neutral[900],
    cursor: disabled ? 'default' : 'pointer',
    display: 'grid',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    textAlign: 'left',
    opacity: disabled ? 0.6 : 1,

    '&:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '1px',
    },
}));

const DeleteActionContainer = styled(Stack)(({ theme }) => ({
    paddingRight: theme.spacing(1),
    display: 'flex',
    alignSelf: 'stretch',
    flexShrink: 0,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 150ms ease',

    '& button': {
        height: '100%',
        borderRadius: '6px',
    },
    '& button:focus-visible': {
        outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
        outlineOffset: '1px',
    },
}));

const ThreadScope = styled(Text)(({ theme }) => ({
    color: theme.palette.ensightful.neutral[600],
    fontSize: '12px',
    lineHeight: '16px',
}));

const NewChatButton = styled(Button)(() => ({
    width: '100%',
    justifyContent: 'flex-start',
}));

export default HugoThreadSidebar;
